import { colors, Icon, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import MDBox from "components/MDBox";
import { formatDateEndOfDay } from "helpers/formatDate";

import { formatCurrency } from "helpers/utils";
import { TypeStatusInscricao } from "interfaces/inscricoes";
import { IRelPaymentsPeriod } from "interfaces/reports";

import MenuMore from "./MenuMore";
import { IDetailsBoleto } from "interfaces/detailsBoleto";
import { IDetailsPix } from "interfaces/detailsPix";
import { useState } from "react";
import ModalDetailsPay from "./ModalDetailsPay";

// imagens
import iconBoleto from "assets/images/icons/bol.png";
import iconPix from "assets/images/icons/pix.png";
import { IDetailsCartaoEfi } from "interfaces/detailsCartao";

interface Props {
  pendentes: IRelPaymentsPeriod[];
  totals: Partial<{
    [k in TypeStatusInscricao]: { qtd: number; total: number };
  }>;
}

type PaymentDetails = IDetailsBoleto | IDetailsPix | IDetailsCartaoEfi | null;

export default function LancamentoPendentes({ pendentes, totals }: Props) {
  const [detailsPaymentSelected, setDetailsPaymentSelected] =
    useState<PaymentDetails>(null);

  function TypePaymentoIcon({
    payment,
  }: {
    payment: IRelPaymentsPeriod;
  }): JSX.Element {
    if (payment.cobrancaPix) return <img src={iconPix} alt="pix" width={45} />;
    if (payment.cobrancaBoleto)
      return <img src={iconBoleto} alt="boleto" width={45} />;
    return <Icon>payment</Icon>;
  }

  const columns: GridColDef<IRelPaymentsPeriod>[] = [
    {
      field: "dataVencimento",
      headerName: "Data Vencimento",
      width: 150,
      align: "center",
      valueFormatter: (params) => formatDateEndOfDay(params.value),
    },
    {
      field: "nInscrição",
      headerName: "N. Inscrição",
      width: 150,
      valueGetter: (params) => params.row.inscricaoEvento.numeroInscricao,
    },
    {
      field: "taxa",
      headerName: "Taxa",
      width: 100,
      align: "right",
      headerAlign: "right",
      valueGetter: (params) => params.row.inscricaoEvento.taxa,
      valueFormatter: (params) => formatCurrency(params.value),
    },
    {
      field: "totalTaxa",
      headerName: "Total Taxa",
      width: 120,
      align: "right",
      headerAlign: "right",
      valueGetter: (params) => params.row.inscricaoEvento.totalTaxa,
      valueFormatter: (params) => formatCurrency(params.value),
    },
    {
      field: "desconto",
      headerName: "Desconto",
      width: 120,
      align: "right",
      headerAlign: "right",
      valueGetter: (params) => params.row.inscricaoEvento.totalDesconto,
      valueFormatter: (params) => formatCurrency(params.value),
    },
    {
      field: "subTotal",
      headerName: "Sub Total",
      width: 120,
      align: "right",
      headerAlign: "right",
      valueGetter: (params) => params.row.inscricaoEvento.subTotal,
      valueFormatter: (params) => formatCurrency(params.value),
    },
    {
      field: "Total",
      headerName: "Total",
      width: 120,
      align: "right",
      headerAlign: "right",
      valueGetter: (params) => params.row.inscricaoEvento.totalInscricao,
      valueFormatter: (params) => formatCurrency(params.value),
    },
    {
      field: "icone",
      headerName: "$",
      width: 50,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => <TypePaymentoIcon payment={params.row} />,
    },
    {
      field: "action",
      headerName: "",
      width: 100,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: (params) => (
        <MenuMore
          payment={params.row}
          detailsPayment={setDetailsPaymentSelected}
        />
      ),
    },
  ];

  return (
    <MDBox sx={{ p: 2 }}>
      <ModalDetailsPay paymentDetails={detailsPaymentSelected} />
      <DataGrid
        sx={{
          flexGrow: 1,
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.grey[300],
          },
        }}
        columnHeaderHeight={40}
        rows={pendentes}
        columns={columns}
        autoHeight
        localeText={{
          noRowsLabel: "Nenhum registro encontrado",
        }}
        density="comfortable"
        disableRowSelectionOnClick
        pageSizeOptions={[10, 20, 50, 100]}
        initialState={{
          pagination: { paginationModel: { page: 0, pageSize: 10 } },
        }}
      />
      <Typography
        variant="subtitle1"
        fontWeight="bold"
        sx={{ mt: 2 }}
        textAlign="left"
      >
        Total: {formatCurrency(totals?.Pendente?.total || 0)}
      </Typography>
      <Typography variant="subtitle2" textAlign="left">
        {`${totals?.Pendente?.qtd || 0} lançamentos`}
      </Typography>
    </MDBox>
  );
}
